/**
 * ReadMore: collapses overflowing text to N lines with an accessible toggle.
 * Usage: call ReadMore.init() once after the DOM is ready.
 */
const ReadMore = (() => {
  let uid = 0;

  function pxPerLine(el) {
    const cs = getComputedStyle(el);
    const lineHeight = parseFloat(cs.lineHeight);
    const fontSize = parseFloat(cs.fontSize);
    // Normalize: some browsers may return "normal" for line-height
    const lh = Number.isFinite(lineHeight) ? lineHeight : 1.5 * fontSize;
    return lh;
  }

  function needsClamp(el, maxLines) {
    const original = el.style.maxHeight;
    el.style.maxHeight = 'none';
    const fullHeight = el.scrollHeight;
    const perLine = pxPerLine(el);
    el.style.maxHeight = original;
    return fullHeight > perLine * maxLines + 1; // tiny buffer
  }

  function enhance(container, opts) {
    const maxLines = Number(container.dataset.readmore) || opts.maxLines;
    container.style.setProperty('--rm-lines', maxLines);

    const content = document.createElement('div');
    content.className = 'rm__content';
    content.innerHTML = container.innerHTML;
    container.innerHTML = '';
    container.appendChild(content);

    const perLine = pxPerLine(content);
    const fontSize = parseFloat(getComputedStyle(content).fontSize) || 16;
    container.style.setProperty('--rm-line-height', (perLine / fontSize).toString());

    const id = `rm-${++uid}`;
    content.id = id;

    const btn = document.createElement('button');
    btn.className = 'rm__btn';
    btn.type = 'button';
    btn.setAttribute('aria-expanded', 'false');
    btn.setAttribute('aria-controls', id);
    btn.textContent = opts.moreText;
    container.appendChild(btn);

    function syncLineHeight() {
      const perLine = pxPerLine(content);
      const fontSize = parseFloat(getComputedStyle(content).fontSize) || 16;
      container.style.setProperty('--rm-line-height', (perLine / fontSize).toString());
    }

    btn.addEventListener('click', () => {
      const expanded = container.classList.toggle('rm--expanded');
      container.dataset.rmUserExpanded = expanded ? 'true' : 'false';
      btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      btn.textContent = expanded ? opts.lessText : opts.moreText;
    });

    function updateState() {
      syncLineHeight();

      const shouldClamp = needsClamp(content, maxLines);
      const userExpanded = container.dataset.rmUserExpanded === 'true';
      const wasClampable = container.dataset.rmClampable === 'true';

      if (!shouldClamp) {
        container.classList.add('rm--expanded');
        container.dataset.rmClampable = 'false';
        btn.style.display = 'none';
        btn.setAttribute('aria-expanded', 'true');
        return;
      }

      container.dataset.rmClampable = 'true';
      btn.style.display = '';

      // If this item has just transitioned from "fits" to "needs clamp",
      // collapse it by default unless the user explicitly opened it.
      if (!wasClampable && !userExpanded) {
        container.classList.remove('rm--expanded');
      }

      const expanded = container.classList.contains('rm--expanded');
      btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      btn.textContent = expanded ? opts.lessText : opts.moreText;
    }

    updateState();

    const ro = new ResizeObserver(() => {
      updateState();
    });

    ro.observe(content);
  }

  return {
    init(options = {}) {
      const opts = {
        selector: '.rm--true',
        maxLines: 4,
        moreText: 'Read more',
        lessText: 'Show less',
        ...options
      };
      document.querySelectorAll(opts.selector).forEach(el => {
        if (!el.classList.contains('rm--ready')) {
          el.classList.add('rm--ready');
          el.classList.add('rm--true'); // ensure base class is present
          enhance(el, opts);
        }
      });
    }
  };
})();

// Kick it off after DOM is ready
if (document.readyState !== 'loading') {
  ReadMore.init({ maxLines: 4 });
} else {
  document.addEventListener('DOMContentLoaded', () => ReadMore.init({ maxLines: 4 }));
}